// src/components/shapeTakeoff/index.js
import React, { useState, useEffect, useContext, useMemo, useCallback, useTransition, memo } from 'react';
import ShapeList_takeoff from './ShapeList_takeoff.js';
import { AuthUserContext } from '../Session';
import { withFirebase } from '../Firebase';
import { withAuthorization } from '../Session';

// Import utilities
import { deduplicateShapes } from '../Replan/utils/drawingUtils';
import { SERVER_URL } from '../../DB_Url.js';
import { getShapeMeasurementData } from '../Replan/utils/measurementUtils';
import { getOrderedShapesFromPageData } from '../Replan/utils/shapePersistence';
import { useProjectSelection } from '../ProjectSelection';


import { METRIC_SCALES, IMPERIAL_SCALES } from '../Replan/constants/scales';
import './shapeTakeoff.css';
import { useTranslation } from 'react-i18next';

// Performance constants
const MAX_SHAPES_PER_VERSION = 500; // Warn if exceeded
const SHAPES_WARNING_THRESHOLD = 2000; // Total shapes warning
const BATCH_SIZE = 50; // For progressive loading

const ShapeListStandalone = ({ firebase }) => {
  const authUser = useContext(AuthUserContext);
  const { t } = useTranslation();

  // --- State for project/version selection ---
  const [projects, setProjects] = useState([]);
  const { selectedProject: globalSelectedProject, setSelectedProject: setGlobalSelectedProject } = useProjectSelection();
  const [projectName, setProjectName] = useState(globalSelectedProject || "");
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingShapes, setLoadingShapes] = useState(false);
  const [shapesByVersion, setShapesByVersion] = useState(new Map()); // Store shapes per version
  const [selectedShapeIds, setSelectedShapeIds] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState([]); // Multiple versions selected
  const [isPending, startTransition] = useTransition();
  const [performanceWarning, setPerformanceWarning] = useState(null);
  const [displayUnitSystem, setDisplayUnitSystem] = useState(null);

  const setProjectNameSynced = useCallback((nextProjectName) => {
    const next = nextProjectName || '';
    setProjectName(next);
    setGlobalSelectedProject(next);
  }, [setGlobalSelectedProject]);

  // Compute allShapesRaw from shapesByVersion for compatibility
  const allShapesRaw = useMemo(() => {
    const allShapes = [];
    shapesByVersion.forEach(shapes => allShapes.push(...shapes));
    return allShapes;
  }, [shapesByVersion]);

  // --- Deduplicate shapes (same as Replan.js) ---
  const deduplicatedShapes = useMemo(() => deduplicateShapes(allShapesRaw), [allShapesRaw]);

  // Performance warning check
  useEffect(() => {
    if (deduplicatedShapes.length > SHAPES_WARNING_THRESHOLD) {
      setPerformanceWarning(`Large dataset: ${deduplicatedShapes.length} shapes. Consider filtering by version.`);
    } else {
      setPerformanceWarning(null);
    }
  }, [deduplicatedShapes.length]);

  // --- Handler for shape selection (same logic as Replan.js) ---
  const handleShapeListItemSelect = useCallback((shapeIds, shouldSelect) => {
    setSelectedShapeIds(prev => {
      const idsToProcess = Array.isArray(shapeIds) ? shapeIds : [shapeIds];

      if (typeof shouldSelect === 'boolean') {
        if (shouldSelect) {
          const newIds = idsToProcess.filter(id => !prev.includes(id));
          return [...prev, ...newIds];
        } else {
          return prev.filter(id => !idsToProcess.includes(id));
        }
      } else {
        const id = idsToProcess[0];
        const isSelected = prev.includes(id);
        if (isSelected) {
          return prev.filter(prevId => prevId !== id);
        } else {
          return [...prev, id];
        }
      }
    });
  }, [setSelectedShapeIds]);

  // --- Handler for updating shape (same as Replan.js) ---
  const handleUpdateShape = (shapeId, updates) => {
    setShapesByVersion(prev => {
      const newMap = new Map(prev);
      for (const [version, shapes] of newMap) {
        const shapeIndex = shapes.findIndex(shape => shape.id === shapeId);
        if (shapeIndex !== -1) {
          const updatedShapes = [...shapes];
          updatedShapes[shapeIndex] = { ...updatedShapes[shapeIndex], ...updates };
          newMap.set(version, updatedShapes);
          break; // Found and updated the shape, exit the loop
        }
      }
      return newMap;
    });
  };


  // --- Project management functions (similar to TakeOff.js) ---
  const saveSelectedProjectToProfile = useCallback(async (projectName) => {
    if (!authUser?.uid || !firebase) return;

    try {
      await firebase.doSetUserPreference(authUser.uid, 'shapelist/selectedProject', projectName);
    } catch (error) {
      console.error('Failed to save selected project:', error);
    }
  }, [authUser, firebase]);

  const loadSelectedProjectFromProfile = useCallback(async () => {
    if (!authUser?.uid || !firebase) return null;

    try {
      return await firebase.doGetUserPreference(authUser.uid, 'shapelist/selectedProject');
    } catch (error) {
      console.error('Failed to load selected project:', error);
      return null;
    }
  }, [authUser, firebase]);

  // --- Fetch only version list for a project (no shapes yet) ---
  const fetchProjectVersions = useCallback(async (projectName) => {
    if (!projectName || !firebase) return;

    setLoadingShapes(true);
    setError(null);
    setPerformanceWarning(null);

    try {
      console.log(`Fetching versions for project: ${projectName}`);
      
      // Use the backend API to get versions (same as PdfControls)
      const versionsUrl = new URL(`${SERVER_URL}/api/versions`);
      versionsUrl.searchParams.append('project', projectName);
      
      const response = await fetch(versionsUrl);
      if (!response.ok) throw new Error('Failed to fetch versions');
      
      const versionNames = await response.json();
      
      if (versionNames.length === 0) {
        console.log(`No versions found for project ${projectName}`);
        setVersions([]);
        setShapesByVersion(new Map());
        setSelectedVersions([]);
        setLoadingShapes(false);
        return;
      }

      setVersions(versionNames);
      console.log(`Found ${versionNames.length} versions:`, versionNames);

      // Auto-select all versions
      if (versionNames.length > 0) {
        setSelectedVersions(versionNames);
        // Fetch shapes for all versions
        for (const version of versionNames) {
          await fetchVersionShapes(projectName, version);
        }
      }
    } catch (err) {
      console.error('Error fetching project versions:', err);
      setError(err.message);
      setVersions([]);
    } finally {
      setLoadingShapes(false);
    }
  }, [firebase]);

  // --- Fetch shapes for a specific version only ---
  const fetchVersionShapes = useCallback(async (projectName, versionName) => {
    if (!projectName || !versionName || !firebase) return;

    // Check if we already have shapes for this version
    if (shapesByVersion.has(versionName) && shapesByVersion.get(versionName).length > 0) {
      console.log(`Using cached shapes for version ${versionName}`);
      return;
    }

    setLoadingShapes(true);
    setError(null);

    try {
      console.log(`Fetching shapes for version: ${versionName}`);
      
      // Get pages info from backend API to get organization
      // The API returns the latest version by default, but includes alternativeVersions
      const infoUrl = new URL(`${SERVER_URL}/api/document/info`);
      infoUrl.searchParams.append('project', projectName);
      
      const infoResponse = await fetch(infoUrl);
      if (!infoResponse.ok) throw new Error('Failed to fetch page info');
      
      const { pages } = await infoResponse.json();
      
      if (pages.length === 0) {
        console.log(`No pages found for project ${projectName}`);
        setShapesByVersion(prev => {
          const newMap = new Map(prev);
          newMap.set(versionName, []);
          return newMap;
        });
        setLoadingShapes(false);
        return;
      }

      // Get organization from first page
      const organization = pages[0].organization;
      if (!organization) {
        console.warn('No organization found in page data, cannot fetch shapes');
        setLoadingShapes(false);
        return;
      }
      
      console.log(`Using organization: ${organization}`);
      
      // Build a map of all page numbers for this version from alternativeVersions
      const versionPageNumbers = new Set();
      pages.forEach(page => {
        if (page.version === versionName) {
          versionPageNumbers.add(page.pageIndex);
        }
        // Also check alternative versions
        (page.alternativeVersions || []).forEach(alt => {
          if (alt.version === versionName) {
            // Need to find the pageIndex for this alternative
            // We'll query Firestore directly for this version
          }
        });
      });
      
      // Query Firestore directly for this version to get all pages
      const pagesRef = firebase.fsdb
        .collection(organization)
        .doc(projectName)
        .collection('versions')
        .doc(versionName)
        .collection('pages');
      
      const pagesSnapshot = await pagesRef.get();
      
      if (pagesSnapshot.empty) {
        console.log(`No pages found in Firestore for version ${versionName}`);
        setShapesByVersion(prev => {
          const newMap = new Map(prev);
          newMap.set(versionName, []);
          return newMap;
        });
        setLoadingShapes(false);
        return;
      }

      // Process shapes from all pages in this version
      const versionShapes = pagesSnapshot.docs.flatMap(pageDoc => {
        if (!pageDoc.exists) return [];

        const pageData = pageDoc.data();
        const pageIndex = parseInt(pageDoc.id, 10);
        
        // Get page info from backend data if available
        const pageInfo = pages.find(p => p.version === versionName && p.pageIndex === pageIndex);
        const pageName = pageInfo?.pageLabel || `Page ${pageIndex}`;
        const pageDpi = pageInfo?.dpi || pageData.dpi || 72;
        
        const pageWidth = pageData.referenceWidth || pageData.width || 0;
        const pageHeight = pageData.referenceHeight || pageData.height || 0;
        
        const scaleKey = pageData.selectedScaleKey;
        const measurementSystem = pageData.measurementSystem || 'metric';
        const scaleList = measurementSystem === 'metric' ? METRIC_SCALES : IMPERIAL_SCALES;
        const scaleProps = scaleList.find(s => s.key === scaleKey) || scaleList[0];

        const pageShapes = getOrderedShapesFromPageData(pageData);

        return pageShapes.map(shape => {
          const shapeDpi = shape?.storedDPI || shape?.originalDPI || pageDpi;
          const ctx = {
            currentScaleProps: scaleProps,
            dpi: shapeDpi,
            imageSize: { width: pageWidth, height: pageHeight },
            displayUnitSystem: pageData.displayUnitSystem || measurementSystem,
          };

          return {
            ...shape,
            measurementContext: ctx,
            pageInfo: {
              name: pageName,
              version: versionName,
              pageIndex: pageIndex,
              organization: organization
            }
          };
        });
      });

      console.log(`Fetched ${versionShapes.length} shapes for version ${versionName}`);

      // Check for performance warning
      if (versionShapes.length > MAX_SHAPES_PER_VERSION) {
        setPerformanceWarning(`Large version: ${versionShapes.length} shapes in ${versionName}.`);
      } else {
        setPerformanceWarning(null);
      }

      // Store shapes for this version (synchronous - not deferred via transition)
      setShapesByVersion(prev => {
        const newMap = new Map(prev);
        newMap.set(versionName, versionShapes);
        return newMap;
      });
    } catch (err) {
      console.error(`Error fetching shapes for version ${versionName}:`, err);
      setError(err.message);
    } finally {
      setLoadingShapes(false);
    }
  }, [firebase, shapesByVersion]);

  const handleProjectSelection = useCallback(async (selectedProjectName) => {
    setProjectNameSynced(selectedProjectName);
    await saveSelectedProjectToProfile(selectedProjectName);
    setError(null);
    setVersions([]);
    setShapesByVersion(new Map());
    setSelectedVersions([]);
    
    // Fetch versions (and first version's shapes) when project is selected
    if (selectedProjectName) {
      fetchProjectVersions(selectedProjectName);
    }
  }, [saveSelectedProjectToProfile, fetchProjectVersions, setProjectNameSynced]);

  useEffect(() => {
    const nextGlobalProject = globalSelectedProject || '';
    if (nextGlobalProject !== projectName) {
      setProjectName(nextGlobalProject);
      setError(null);
      setVersions([]);
      setShapesByVersion(new Map());
      setSelectedVersions([]);
    }
  }, [globalSelectedProject, projectName]);

  // Get shapes for all currently selected versions
  const currentVersionShapes = useMemo(() => {
    const allShapes = [];
    selectedVersions.forEach(v => {
      allShapes.push(...(shapesByVersion.get(v) || []));
    });
    return allShapes;
  }, [selectedVersions, shapesByVersion]);

  // Handle version selection changes - supports multi-select and lazy shape fetching
  const handleVersionsChange = useCallback(async (newVersions) => {
    setSelectedVersions(newVersions);
    // Fetch shapes for newly selected versions that aren't cached yet
    for (const version of newVersions) {
      if (!shapesByVersion.has(version) || (shapesByVersion.get(version) || []).length === 0) {
        await fetchVersionShapes(projectName, version);
      }
    }
  }, [shapesByVersion, projectName, fetchVersionShapes]);

  // --- Load projects on mount (similar to TakeOff.js) ---
  useEffect(() => {
    if (!authUser?.roles) {
      setProjects([]);
      return;
    }

    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const userOrgs = Object.keys(authUser.roles).filter(key => authUser.roles[key] !== null);
        if (userOrgs.length === 0) {
          setProjects([]);
          setLoadingProjects(false);
          return;
        }

        const projectsUrl = new URL(`${SERVER_URL}/api/projects`);
        projectsUrl.searchParams.append('organisations', userOrgs.join(','));

        const response = await fetch(projectsUrl);
        if (!response.ok) throw new Error('Failed to fetch projects');

        const projectList = await response.json();
        setProjects(projectList);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        setError('Failed to load projects');
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [authUser, loadSelectedProjectFromProfile, fetchProjectVersions, setProjectNameSynced]);

  useEffect(() => {
    if (!projectName) return;
    fetchProjectVersions(projectName);
  }, [projectName]);

  return (
    <main className="shapetakeoff-container" style={{padding: 0}}>
      {/* Header */}
      {/* <div className="shapetakeoff-header">
        <h2>{t('inventory.shape_takeoff_title')}</h2>

      </div> */}

      {/* Error/Warning Messages */}
      {error && (
        <div className="shapetakeoff-error">
          Error: {error}
        </div>
      )}
      {performanceWarning && (
        <div className="shapetakeoff-warning">
          ⚠️ {performanceWarning}
        </div>
      )}

      {/* Main Content */}
      <div className="shapetakeoff-main-content">
        {!projectName ? (
          <div className="shapetakeoff-placeholder">
            <h3>{t('inventory.select_a_project_to_view_shapes')}</h3>
            <p>{t('inventory.choose_project_from_dropdown')}</p>
          </div>
        ) : loadingShapes && versions.length === 0 ? (
          <div className="shapetakeoff-placeholder">
            <span aria-busy="true">{t('inventory.loading_shapes')}</span>
          </div>
        ) : versions.length === 0 ? (
          <div className="shapetakeoff-placeholder">
            <h3>{t('inventory.no_versions_found')}</h3>
          </div>
        ) : (
          // Always render ShapeList_takeoff when versions exist so the toolbar
          // (including version selector dropdown) is always accessible.
          <div className="shapetakeoff-shapelist-wrapper">
            <ShapeList_takeoff
              projectName={projectName}
              shapes={currentVersionShapes}
              selectedShapeIds={selectedShapeIds}
              onShapeSelect={handleShapeListItemSelect}
              onUpdateShape={handleUpdateShape}
              currentScaleProps={undefined}
              dpi={undefined}
              imageSize={undefined}
              authUser={authUser}
              firebase={firebase}
              storageKeyPrefix={`shapelist:${projectName}:${selectedVersions.join(',')}`}
              versions={versions}
              selectedVersions={selectedVersions}
              onVersionsChange={handleVersionsChange}
              loadingVersionShapes={loadingShapes}
              isPending={isPending}
              displayUnitSystem={displayUnitSystem}
              onDisplayUnitSystemChange={setDisplayUnitSystem}
              embedded
            />
            {selectedVersions.length === 0 && (
              <div className="shapetakeoff-placeholder">
                <h3>{t('inventory.select_a_version')}</h3>
              </div>
            )}
            {selectedVersions.length > 0 && loadingShapes && currentVersionShapes.length === 0 && (
              <div className="shapetakeoff-placeholder">
                <span aria-busy="true">{t('inventory.loading_shapes_for', { version: selectedVersions[0] })}</span>
              </div>
            )}
            {selectedVersions.length > 0 && !loadingShapes && currentVersionShapes.length === 0 && (
              <div className="shapetakeoff-placeholder">
                <h3>{t('inventory.no_shapes_found')}</h3>
                <p>{t('inventory.version_has_no_annotated_shapes', { version: selectedVersions.join(', ') })}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

// Authorization condition - require authenticated user
const condition = authUser => !!authUser;

export default withAuthorization(condition)(withFirebase(ShapeListStandalone));
